import {
  BriefingCampaignType,
  BriefingPlatform,
  ClaudeTemplate,
  ResolvedTemplate,
} from "../types";
import { adformTemplate } from "./adform";
import { adrollTemplate } from "./adroll";
import { amazonDspTemplate } from "./amazonDsp";
import { chatgptAdsTemplate } from "./chatgptAds";
import { directBuyTemplate } from "./directBuy";
import { dv360Template } from "./dv360";
import { dv360YouTubeTemplate } from "./dv360YouTube";
import { genericTemplate } from "./generic";
import { googleAdsDemandGenTemplate } from "./googleAdsDemandGen";
import { googleAdsPmaxTemplate } from "./googleAdsPmax";
import { googleAdsSearchTemplate } from "./googleAdsSearch";
import { hivestackTemplate } from "./hivestack";
import { linkedinTemplate } from "./linkedin";
import { metaTemplate } from "./meta";
import { outbrainTemplate } from "./outbrain";
import { pinterestTemplate } from "./pinterest";
import { redditTemplate } from "./reddit";
import { spotifyAdsTemplate } from "./spotifyAds";
import { taboolaTemplate } from "./taboola";
import { tiktokTemplate } from "./tiktok";
import { xAdsTemplate } from "./xAds";

export const CLAUDE_TEMPLATES: ClaudeTemplate[] = [
  dv360Template,
  dv360YouTubeTemplate,
  amazonDspTemplate,
  hivestackTemplate,
  spotifyAdsTemplate,
  outbrainTemplate,
  taboolaTemplate,
  metaTemplate,
  tiktokTemplate,
  googleAdsSearchTemplate,
  googleAdsPmaxTemplate,
  googleAdsDemandGenTemplate,
  redditTemplate,
  linkedinTemplate,
  xAdsTemplate,
  pinterestTemplate,
  chatgptAdsTemplate,
  adrollTemplate,
  adformTemplate,
  directBuyTemplate,
  genericTemplate,
];

type Resolver = (
  campaignType?: BriefingCampaignType | null,
  nativeAdProvider?: string | null
) => ResolvedTemplate;

const withTemplate =
  (template: ClaudeTemplate, ...notes: string[]): Resolver =>
  () => ({ template, notes });

const ctvDeal = (publisher: string): Resolver =>
  withTemplate(
    dv360Template,
    `Inventario de ${publisher} en CTV. Normalmente se compra con un deal (Programmatic Guaranteed o PMP) de ${publisher} en DV360: el deal ID no viene en el briefing. Búscalo en "Inventory" → "My inventory" filtrando por ${publisher}; si no hay ninguno activo o hay varios, para y pregunta.`,
    'Line item de tipo "Over-the-top" (CTV) o el que exija el deal.'
  );

const META_PLACEMENTS = {
  FACEBOOK:
    "Placements: solo Facebook (desactiva Instagram, Messenger y Audience Network).",
  INSTAGRAM:
    "Placements: solo Instagram (desactiva Facebook, Messenger y Audience Network).",
  FACEBOOK_AND_INSTAGRAM:
    "Placements: Facebook e Instagram (desactiva Messenger, Threads y Audience Network, salvo que las notas digan otra cosa).",
};

/**
 * Plataforma de briefing → plantilla. Es un Record para que TypeScript falle si se
 * añade una plataforma nueva al enum sin decidir con qué plantilla se monta.
 * Las plataformas reales salen de /platform-mappings (getPlatformMappings en el backend).
 */
const RESOLVERS: Record<BriefingPlatform, Resolver> = {
  BEAPOLO: withTemplate(
    dv360Template,
    'Formatos rich media de BeApolo (familia IABA: Dynamic, Engage, Playable, Feel). Lo habitual es que las creatividades sean tags de terceros del adserver de BeApolo: súbelos como "Third-party tag" (o "Third-party video / VAST" si el tipo acaba en _VIDEO). Si los enlaces son archivos y no tags, dímelo.',
    "Line item de tipo Display, o Video si el tipo de campaña acaba en _VIDEO."
  ),
  BEAPOLO_DCO: withTemplate(
    dv360Template,
    "BEAPOLO_DCO (creatividad dinámica) no aparece en /platform-mappings: se asume DV360 con tags del adserver de BeApolo. Confírmamelo antes de crear nada."
  ),
  BEAPOLO_YOUTUBE: withTemplate(dv360YouTubeTemplate),
  BEAPOLO_YOUTUBE_CTV: withTemplate(
    dv360YouTubeTemplate,
    'YouTube solo en TV conectadas: en el line item limita Devices a "Connected TV".'
  ),
  BEAPOLO_NETFLIX: ctvDeal("Netflix"),
  BEAPOLO_DISNEY_PLUS: ctvDeal("Disney+"),
  BEAPOLO_MOVISTAR: ctvDeal("Movistar Plus+"),
  BEAPOLO_LG: ctvDeal("LG Ads (Smart TV de LG)"),
  BEAPOLO_SAMSUNG_TV: ctvDeal("Samsung Ads (Smart TV de Samsung)"),
  BEAPOLO_RADIO: (campaignType) =>
    campaignType === "OFFLINE_RADIO"
      ? {
          template: directBuyTemplate,
          notes: ["Radio offline: cuñas compradas directamente a la emisora."],
        }
      : {
          template: dv360Template,
          notes: [
            "Radio digital en DV360: line item de tipo Audio, con el inventario de audio o radio online que digan las notas (deals o exchanges). Si no lo dicen, pregunta.",
          ],
        },
  BEAPOLO_AMAZON_PRIME: withTemplate(
    amazonDspTemplate,
    'Inventario de Prime Video (Streaming TV). Los nombres llevan "PRIME" a propósito.'
  ),
  BEAPOLO_TWITCH: withTemplate(
    amazonDspTemplate,
    "Inventario de Twitch (vídeo / streaming)."
  ),
  BEAPOLO_DOOH: withTemplate(hivestackTemplate),
  BEAPOLO_OOH: (campaignType) =>
    campaignType === "OOH_COMPRA_PROGRAMATICA"
      ? {
          template: hivestackTemplate,
          notes: [
            "Exterior programático: se asume Hivestack porque es la DSP de DOOH que usamos. Si esta línea va por otra herramienta, para y pregunta.",
          ],
        }
      : {
          template: directBuyTemplate,
          notes: [
            "Exterior (OOH) comprado directamente al exclusivista o al propietario del circuito.",
          ],
        },
  BEAPOLO_SPOTIFY: withTemplate(spotifyAdsTemplate),
  BEAPOLO_PODCAST: withTemplate(
    spotifyAdsTemplate,
    "Podcast: /platform-mappings no indica la plataforma real. Se asume Spotify Ads Manager con inventario de podcasts; si esta línea se compra en otro sitio (directamente a un podcast o a otra red), para y pregunta."
  ),
  BEAPOLO_PRENSA: (campaignType) =>
    withTemplate(
      directBuyTemplate,
      campaignType === "PRENSA_OFFLINE"
        ? "Prensa en papel: inserción comprada directamente a la cabecera."
        : "Prensa online: espacio comprado directamente al medio digital."
    )(),
  BEAPOLO_SKIN: withTemplate(
    directBuyTemplate,
    "Skin (formato de branding que viste la portada de un medio): se contrata directamente con el medio."
  ),
  BEAPOLO_OTT: withTemplate(
    directBuyTemplate,
    "OTT / CTV sin plataforma indicada en /platform-mappings. Se asume compra directa; si se compra de forma programática (por ejemplo, con un deal en DV360), dímelo y lo cambiamos."
  ),
  ATRESMEDIA: withTemplate(
    directBuyTemplate,
    "OTT de Atresmedia (ATRESplayer): compra directa al grupo."
  ),
  TRES_CAT: withTemplate(
    directBuyTemplate,
    "OTT de 3Cat: compra directa al medio."
  ),
  MEDIASET: withTemplate(
    directBuyTemplate,
    "OTT de Mediaset (Mitele): compra directa al grupo."
  ),
  BEAPOLO_REDDIT: withTemplate(redditTemplate),
  BEAPOLO_CHATGPT: withTemplate(chatgptAdsTemplate),
  NATADS: (campaignType, provider) => {
    const typeNote = `Tipo nativo: ${campaignType ?? "no indicado"}.`;
    if (provider === "OUTBRAIN")
      return { template: outbrainTemplate, notes: [typeNote] };
    if (provider === "TABOOLA")
      return { template: taboolaTemplate, notes: [typeNote] };
    return {
      template: genericTemplate,
      notes: [
        "Nativo (NATADS) sin proveedor indicado en el briefing: puede ser Outbrain o Taboola. Pregunta cuál antes de empezar.",
        typeNote,
      ],
    };
  },
  GOOGLE_ADS_SEM: withTemplate(googleAdsSearchTemplate),
  GOOGLE_ADS_PMAX: withTemplate(googleAdsPmaxTemplate),
  GOOGLE_ADS_DEMANDGEN: withTemplate(googleAdsDemandGenTemplate),
  FACEBOOK: withTemplate(metaTemplate, META_PLACEMENTS.FACEBOOK),
  INSTAGRAM: withTemplate(metaTemplate, META_PLACEMENTS.INSTAGRAM),
  FACEBOOK_AND_INSTAGRAM: withTemplate(
    metaTemplate,
    META_PLACEMENTS.FACEBOOK_AND_INSTAGRAM
  ),
  TIKTOK: withTemplate(tiktokTemplate),
  X_TWITTER: withTemplate(xAdsTemplate),
  LINKEDIN: withTemplate(linkedinTemplate),
  PINTEREST: withTemplate(pinterestTemplate),
  ADROLL: withTemplate(adrollTemplate),
  ADFORM: withTemplate(adformTemplate),
  UNDETERMINED: withTemplate(genericTemplate),
};

export const resolveTemplate = (
  platform: BriefingPlatform | string | null | undefined,
  campaignType?: BriefingCampaignType | null,
  nativeAdProvider?: string | null
): ResolvedTemplate => {
  const resolver = platform
    ? RESOLVERS[platform as BriefingPlatform]
    : undefined;
  if (!resolver) {
    return {
      template: genericTemplate,
      notes: [
        `La plataforma "${platform ?? "sin indicar"}" no tiene plantilla en BeApolo.`,
      ],
    };
  }
  return resolver(campaignType, nativeAdProvider);
};
